import { useNavigate } from 'react-router'
import { Clock } from 'lucide-react'
import { Button } from '../../platform/primitives/Button'
import { AuthCard } from './AuthCard'
import './auth.css'

/**
 * "We signed you out" — rendered on whatever route carries the signal that a
 * session has ended. This component does not decide when that is; it is
 * given the signal by whatever navigates here (a 401 from the API, a Firebase
 * token that failed to refresh), exactly as the design's own copy implies —
 * see below for why that copy is not transcribed whole.
 *
 * **Two things the comp draws are deliberately not built here.**
 *
 * The identity chip — an avatar, "Sushil Chaudhari", an email address — needs
 * a signed-out user's name and email to still be around to display, which
 * means persisting them across sign-out. That is a privacy decision, not a
 * styling one: it is choosing to retain personally identifying information
 * past the point the user asked to be signed out, and it is not this card's
 * to make silently. Omitted, and flagged for the team to decide. Its absence
 * has one further consequence: with no chip, "Sign back in" and "Sign in as
 * someone else" would do the exact same thing — there is no other account on
 * screen to switch away from — so the second control is omitted with it
 * rather than kept as a dead duplicate of the first.
 *
 * **No idle timer.** The comp's reassurance sentence names "30 minutes of
 * inactivity", but no such timeout exists anywhere in this app, and building
 * one is a feature in its own right with its own product questions — what
 * counts as activity, whether a backgrounded tab counts, what happens to an
 * open form when it fires. Inventing a number here would be describing a
 * mechanism that does not exist. The sentence below keeps the part that is
 * true regardless of cause — unsaved input survives a sign-out — and drops
 * the invented figure.
 */
export function SessionExpired() {
  const navigate = useNavigate()

  return (
    <AuthCard
      icon={
        <span className="auth-icon auth-icon--neutral">
          <Clock size={24} strokeWidth={1.9} aria-hidden="true" />
        </span>
      }
      title="We signed you out"
      centered
    >
      <p className="auth-card__note">
        Anything you&apos;d typed is still here — sign back in and it will be waiting.
      </p>
      <Button variant="primary" size="form" onClick={() => navigate('/signin')}>
        Sign back in
      </Button>
    </AuthCard>
  )
}
